'use client';

import { useState, useEffect } from 'react';
import { ListFilter, ChevronsUpDown } from 'lucide-react';
import { isMobile } from '@util/index';

type FloatingMobileFilterProps = {
  onFilterClick: () => void;
  onSortClick?: () => void;
  hasActiveFilters?: boolean;
  filterSectionRef?: React.RefObject<HTMLDivElement | null>;
};

export default function FloatingMobileFilter({
  onFilterClick,
  onSortClick,
  hasActiveFilters = false,
  filterSectionRef,
}: FloatingMobileFilterProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isMobile()) return;

    const handleScroll = () => {
      if (!filterSectionRef?.current) {
        // If ref is not available, check again after a short delay
        return;
      }

      const filterSection = filterSectionRef.current;
      const rect = filterSection.getBoundingClientRect();

      // Get the bottom position of the filter section relative to the viewport
      const filterSectionBottom = rect.bottom;

      // Show floating filter when the filter section is scrolled out of view (above viewport)
      // We show it when the bottom of the filter section is above the top of the viewport
      if (filterSectionBottom < 0) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Use requestAnimationFrame for better performance
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    handleScroll(); // Check initial position
    // Also check on resize in case layout changes
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [filterSectionRef]);

  if (!isMobile()) return null;

  return (
    <div
      className={`fixed bottom-20 left-1/2 z-[100] -translate-x-1/2 transition-all duration-300 ease-in-out sm:hidden ${
        isVisible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-8 opacity-0'
      }`}
    >
      <div className="flex items-center rounded-full bg-brand-secondary px-12 py-5 shadow-lg">
        <button
          type="button"
          onClick={onSortClick || onFilterClick}
          className="flex items-center gap-x-3 text-white"
        >
          <ChevronsUpDown className="h-6 w-6" />
          <span className="text-[1.8rem] font-semibold">Sort</span>
        </button>

        <div className="mx-6 h-7 w-[1px] bg-white/30" />

        <button
          type="button"
          onClick={onFilterClick}
          className="relative flex items-center gap-x-3 text-white"
        >
          {hasActiveFilters && (
            <div className="absolute -right-1 -top-1 h-5 w-5 rounded-full bg-red-500" />
          )}
          <ListFilter className="h-6 w-6" />
          <span className="text-[1.8rem] font-semibold">Filter</span>
        </button>
      </div>
    </div>
  );
}
